import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { FaDownload, FaSync, FaFileImport, FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { shipmentService } from '../../../services/shipmentService';
import { activityTracker } from '../../../services/activityTracker';
import { notify } from '../../../utils/notifications';
import ShipmentCard from './ShipmentCard';
import { StatsGrid, StatCard, FilterBar, SearchInput, FilterSelect, ActionButton, SkeletonCard, EmptyState, ModalOverlay, ModalContent, ModalTextarea, ModalActions, ImportResultItem, } from './IFSShippingDashboard.styles';
const ShipmentsTab = ({ isMockMode }) => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const [continuationToken, setContinuationToken] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importText, setImportText] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const fetchShipments = useCallback(async (append = false) => {
        try {
            if (!append)
                setLoading(true);
            setError(null);
            const res = await shipmentService.list({
                status: statusFilter,
                search: searchTerm || undefined,
                pageSize: 20,
                continuationToken: append ? (continuationToken || undefined) : undefined,
            });
            if (res.ok && res.data) {
                setShipments(prev => append ? [...prev, ...(res.data?.items || [])] : (res.data?.items || []));
                setContinuationToken(res.data.continuationToken);
                setTotalCount(res.data.totalCount);
            }
            else {
                setError(res.error || 'Failed to load shipments');
            }
        }
        catch (err) {
            setError(err.message || 'Network error');
        }
        finally {
            setLoading(false);
        }
    }, [statusFilter, searchTerm, continuationToken]);
    useEffect(() => {
        fetchShipments();
    }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const timer = setTimeout(() => fetchShipments(), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps
    const stats = {
        total: shipments.length,
        inTransit: shipments.filter(s => s.status === 'in_transit').length,
        delivered: shipments.filter(s => s.status === 'delivered').length,
        drafts: shipments.filter(s => s.status === 'draft').length,
    };
    const handleRefresh = async (id) => {
        try {
            const res = await shipmentService.refresh(id);
            if (res.ok && res.data?.shipment) {
                setShipments(prev => prev.map(s => s.id === id ? res.data.shipment : s));
                notify.success('Shipment refreshed');
                activityTracker.trackAction('shipment-refresh', 'Refreshed shipment tracking', { shipmentId: id });
            }
            else {
                notify.error(res.error || 'Refresh failed');
            }
        }
        catch {
            notify.error('Failed to refresh shipment');
        }
    };
    const handleDelete = async (id) => {
        if (!window.confirm('Delete this draft shipment?'))
            return;
        try {
            const res = await shipmentService.delete(id);
            if (res.ok) {
                setShipments(prev => prev.filter(s => s.id !== id));
                notify.success('Draft deleted');
                activityTracker.trackAction('shipment-draft-delete', 'Deleted draft shipment', { shipmentId: id });
            }
            else {
                notify.error(res.error || 'Delete failed');
            }
        }
        catch {
            notify.error('Failed to delete shipment');
        }
    };
    const handleImport = async () => {
        const trackingNumbers = importText.split('\n').map(s => s.trim()).filter(Boolean);
        if (trackingNumbers.length === 0)
            return;
        if (trackingNumbers.length > 10) {
            notify.error('Maximum 10 tracking numbers per batch');
            return;
        }
        setImporting(true);
        setImportResults(null);
        try {
            const res = await shipmentService.import(trackingNumbers);
            if (res.data) {
                setImportResults(res.data.results);
                activityTracker.trackAction('shipment-import', `Imported ${res.data.imported} shipments`, { imported: res.data.imported, failed: res.data.failed });
                if (res.data.imported > 0)
                    fetchShipments();
            }
        }
        catch {
            notify.error('Import failed');
        }
        finally {
            setImporting(false);
        }
    };
    const handleExportCSV = () => {
        const headers = ['Tracking #', 'Recipient', 'Destination', 'Service', 'Status', 'Date', 'Declared Value'];
        const rows = shipments.map(s => [
            s.trackingNumber || 'N/A',
            s.recipient?.name || '',
            `${s.recipient?.city || ''}, ${s.recipient?.state || ''}`,
            s.package?.serviceType || '',
            s.status,
            s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '',
            s.package?.declaredValue ? `$${s.package.declaredValue.toLocaleString()}` : '',
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `shipments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };
    return (_jsxs("div", { children: [_jsxs(StatsGrid, { children: [_jsxs(StatCard, { "$accent": "#3b82f6", children: [_jsx("h3", { children: stats.total }), _jsx("p", { children: "Total Shipments" })] }), _jsxs(StatCard, { "$accent": "#f59e0b", children: [_jsx("h3", { children: stats.inTransit }), _jsx("p", { children: "In Transit" })] }), _jsxs(StatCard, { "$accent": "#10b981", children: [_jsx("h3", { children: stats.delivered }), _jsx("p", { children: "Delivered" })] }), _jsxs(StatCard, { "$accent": "#9ca3af", children: [_jsx("h3", { children: stats.drafts }), _jsx("p", { children: "Drafts" })] })] }), _jsxs(FilterBar, { children: [_jsx(SearchInput, { placeholder: "Search by tracking # or recipient...", value: searchTerm, onChange: e => setSearchTerm(e.target.value) }), _jsxs(FilterSelect, { value: statusFilter, onChange: e => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Statuses" }), _jsx("option", { value: "draft", children: "Draft" }), _jsx("option", { value: "imported", children: "Imported" }), _jsx("option", { value: "in_transit", children: "In Transit" }), _jsx("option", { value: "delivered", children: "Delivered" }), _jsx("option", { value: "exception", children: "Exception" }), _jsx("option", { value: "voided", children: "Voided" })] }), _jsxs(ActionButton, { "$variant": "primary", onClick: () => setShowImportModal(true), children: [_jsx(FaFileImport, {}), " Import"] }), _jsxs(ActionButton, { onClick: handleExportCSV, children: [_jsx(FaDownload, {}), " Export CSV"] }), _jsxs(ActionButton, { onClick: () => fetchShipments(), children: [_jsx(FaSync, {}), " Refresh"] })] }), error && (_jsxs("div", { style: { background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', color: '#ef4444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsx("span", { children: error }), _jsx(ActionButton, { "$variant": "danger", onClick: () => fetchShipments(), style: { padding: '0.4rem 0.75rem' }, children: "Retry" })] })), loading ? (_jsxs(_Fragment, { children: [_jsx(SkeletonCard, {}), _jsx(SkeletonCard, {}), _jsx(SkeletonCard, {})] })) : shipments.length === 0 ? (_jsxs(EmptyState, { children: [_jsx("h3", { children: "No shipments found" }), _jsx("p", { children: searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Import tracking numbers or create a new shipment to get started' }), _jsxs(ActionButton, { "$variant": "primary", onClick: () => setShowImportModal(true), children: [_jsx(FaFileImport, {}), " Import Tracking Numbers"] })] })) : (_jsxs(_Fragment, { children: [shipments.map(shipment => (_jsx(ShipmentCard, { shipment: shipment, expanded: expandedId === shipment.id, onToggleExpand: () => setExpandedId(expandedId === shipment.id ? null : shipment.id), onRefresh: () => handleRefresh(shipment.id), onDelete: () => handleDelete(shipment.id) }, shipment.id))), continuationToken && (_jsx("div", { style: { textAlign: 'center', marginTop: '1rem' }, children: _jsx(ActionButton, { onClick: () => fetchShipments(true), children: "Load More" }) }))] })), showImportModal && (_jsx(ModalOverlay, { onClick: () => !importing && setShowImportModal(false), children: _jsxs(ModalContent, { onClick: e => e.stopPropagation(), children: [_jsx("h2", { children: "Import Tracking Numbers" }), _jsx("p", { children: "Enter tracking numbers, one per line (max 10):" }), _jsx(ModalTextarea, { value: importText, onChange: e => setImportText(e.target.value), placeholder: "748912345678\n270012345678\n...", disabled: importing }), importResults && (_jsx("div", { style: { marginTop: '1rem' }, children: importResults.map((r, i) => (_jsxs(ImportResultItem, { "$success": r.success, "$duplicate": r.duplicate, children: [r.success ? _jsx(FaCheck, {}) : r.duplicate ? _jsx(FaExclamationTriangle, {}) : _jsx(FaTimes, {}), _jsx("span", { className: "tracking", children: r.trackingNumber }), !r.success && _jsxs("span", { style: { fontSize: '0.8rem' }, children: ["- ", r.error] })] }, i))) })), _jsxs(ModalActions, { children: [_jsx(ActionButton, { onClick: () => { setShowImportModal(false); setImportText(''); setImportResults(null); }, disabled: importing, children: "Cancel" }), _jsx(ActionButton, { "$variant": "primary", onClick: handleImport, disabled: importing || !importText.trim(), children: importing ? 'Importing...' : 'Import' })] })] }) }))] }));
};
export default ShipmentsTab;
